<template>
    <div v-if="widgetReady" class="container consumer-wizard-root">
        <icon-alert class="consumer-wizard-tile">
            <template slot="icon">
                <i class="fa fa-user" aria-hidden="true"></i>
            </template>
            <template slot="heading">
                <div class="enrollment-signup-heading-row">
                    <span class="enrollment-signup-heading-text">
                        Welcome {{ vm.contactInfo.firstName }}
                        <span v-if="hasValue(vm.contactInfo.middleInitial)">
                            {{ vm.contactInfo.middleInitial }}
                        </span>
                        {{ vm.contactInfo.lastName }}
                    </span>
                    <a
                        class="enrollment-signup-help"
                        target="_blank"
                        rel="noopener noreferrer"
                        href="https://accresa.zendesk.com/hc/en-us/articles/4412176070679-PLACEHOLDER-Video-Initial-Landing-Page"
                    >
                        <span class="wl-interactive">HELP <i class="fa fa-question-circle" aria-hidden="true"></i></span>
                    </a>
                </div>
            </template>
            <div class="wizard-section">
                <div class="wizard-form">
                    Welcome to <span v-company-white-label="companyId"></span>,
                    your new source for exceptional, affordable, and personalized care.
                    The following information is required to verify your
                    coverage information and create your account.
                    After you set up your account, you can search for and select a plan.
                    If any of the fields are pre-filled, please review that
                    all information is correct and make changes if necessary.
                </div>
            </div>
        </icon-alert>

        <icon-alert
            v-if="vm.customBannerText != null"
            class="consumer-wizard-tile enrollment-custom-banner-card"
            role="alert"
        >
            <template slot="icon">
                <i class="fa fa-exclamation"></i>
            </template>
            <span v-html="vm.customBannerText"></span>
        </icon-alert>

        <bar-card class="consumer-wizard-tile consumer-wizard-bar-card">
            <template slot="header">
                Primary Contact
            </template>
            <template slot="body">
                <div class="wizard-section">
                    <div class="wizard-form">
                        <account-info v-model="vm.contactInfo"></account-info>
                    </div>
                </div>
            </template>
        </bar-card>

        <bar-card class="consumer-wizard-tile consumer-wizard-bar-card">
            <template slot="header">
                Social Security Number &amp; Date of Birth
            </template>
            <template slot="body">
                <div class="wizard-section">
                    <div class="wizard-form">
                        <identity-info v-model="vm" :ssn-required="ssnRequired"></identity-info>
                    </div>
                </div>
            </template>
        </bar-card>

        <bar-card class="consumer-wizard-tile consumer-wizard-bar-card">
            <template slot="header">
                Home Address
            </template>
            <template slot="body">
                <div class="wizard-section">
                    <div class="wizard-form">
                        <ac-address v-model="vm.mainModel.address"></ac-address>
                    </div>
                </div>
            </template>
        </bar-card>

        <bar-card v-if="dateOption" class="consumer-wizard-tile consumer-wizard-bar-card">
            <template slot="header">
                Effective Start Date
            </template>
            <template slot="body">
                <div class="wizard-section">
                    <div class="wizard-form">
                        <effective-date
                            v-model="vm.mainModel.effStartDate"
                            :first-of-next-month="firstOfNextMonth"
                        ></effective-date>
                    </div>
                </div>
            </template>
        </bar-card>

        <bar-card v-if="allowDependents" class="consumer-wizard-tile consumer-wizard-bar-card">
            <template slot="header">
                Additional Family Members
            </template>
            <template slot="body">
                <div class="wizard-section">
                    <div class="wizard-form">
                        <add-dependent ref="addDependents" :hide-section-title="true" :ssn-required="ssnRequired" :company-id="companyId"></add-dependent>
                    </div>
                </div>
            </template>
        </bar-card>

        <bar-card v-if="hasReferralSurvey" class="consumer-wizard-tile consumer-wizard-bar-card">
            <template slot="header">
                How did you hear about us?
            </template>
            <template slot="body">
                <div class="wizard-section">
                    <div class="wizard-form">
                        <referral-survey ref="referralSurvey" v-model="vm"></referral-survey>
                    </div>
                </div>
            </template>
        </bar-card>

        <bar-card v-if="hasBusinessOwnerSurvey" class="consumer-wizard-tile consumer-wizard-bar-card">
            <template slot="header">
                Business Owner Survey
            </template>
            <template slot="body">
                <div class="wizard-section">
                    <div class="wizard-form">
                        <business-owner-survey ref="businessOwnerSurvey" v-model="vm"></business-owner-survey>
                    </div>
                </div>
            </template>
        </bar-card>

        <bar-card class="consumer-wizard-tile consumer-wizard-bar-card">
            <template slot="header">
                Login Credentials
            </template>
            <template slot="body">
                <div class="wizard-section">
                    <div class="wizard-form" style="padding-bottom: 15px;">
                        <login-credentials v-model="vm.userViewModel"></login-credentials>
                    </div>
                </div>
            </template>
        </bar-card>

        <icon-alert class="consumer-wizard-tile">
            <template slot="icon">
                <i class="fa fa-star" aria-hidden="true"></i>
            </template>
            <div class="wizard-section">
                <div class="wizard-form consumer-wizard-terms-form">
                    <div class="consumer-wizard-terms-centered">
                        <div class="consumer-wizard-terms-main">
                            <terms-and-conditions v-model="termsAccepted"></terms-and-conditions>
                            <small class="acressaRedText" v-if="acceptTermsError">Must agree to terms of service by checking the above box.</small>
                        </div>
                        <div class="consumer-wizard-pill-wrap">
                            <pill-button size="lg" class="consumer-wizard-save-btn" @click="signUp">
                                Sign Up
                            </pill-button>
                        </div>
                    </div>
                </div>
            </div>
        </icon-alert>
    </div>
</template>

<script>
    import AccountInfo from "./AccountInfo.vue";
    import LoginCredentials from "./LoginCredentials.vue";
    import TermsAndConditions from "./TermsAndConditions.vue";
    import {configTables} from "../../../../global/configTables.js";
    import EffectiveDate from "./EffectiveDate.vue";
    import {api} from "../../../../global/framework/api.js";
    import AddDependent from "../../../../components/shared/consumer/AddDependent.vue";
    import ReferralSurvey from "./ReferralSurvey.vue";
    import BusinessOwnerSurvey from "./BusinessOwnerSurvey.vue";
    import IdentityInfo from "./IdentityInfo.vue";
    import {common} from "../../../../global/common.js";
    import {GotoSearchMixin} from "../../mixins/GotoSearchMixin.js";
    import { notifications } from "../../../../global/notifications";
    import BarCard from "../../../member/components/dashboard/BarCard.vue";
    import IconAlert from "../../../member/components/dashboard/IconAlert.vue";
    import PillButton from "../../../member/components/dashboard/PillButton.vue";
    import AcAddress from "../../../../components/custom/AcAddress.vue";

    export default {
        name: "SignupDashboard",
        props: {
            companyId: {type: [Number], required: true},
            token: {type: [String], required: true}
        },
        components: {
            IdentityInfo,
            EffectiveDate,
            TermsAndConditions,
            LoginCredentials,
            AccountInfo,
            AddDependent,
            ReferralSurvey,
            BusinessOwnerSurvey,
            BarCard,
            IconAlert,
            PillButton,
            AcAddress,
        },
        mixins: [GotoSearchMixin],
        data() {
            return {
                widgetReady: false,
                vm: {},
                today: null,
                dateOption: true,
                acceptTermsError: false,
                termsAccepted: false,
                allowDependents: true,
                hasReferralSurvey: false,
                hasBusinessOwnerSurvey: false,
                ssnRequired: false
            };

        },
        methods: {
            async setVm() {
                configTables.getCoverageOptions().then((options) => {
                    this.coverageTypes = options
                });
                let url = `ProviderSearch/${this.companyId}/GetSignUpTemplate`;
                url = common.appendUrlParameter(url, 'token', this.token);
                let data = await $.ajax({type: "Get", url: url});
                this.firstOfNextMonth = data.firstOfNextMonth;
                this.mapData('vm', data.consumerWizardVM);
                this.dateOption = data.dateOption;
                this.allowDependents = data.allowDependents;
                this.hasReferralSurvey = data.hasReferralSurvey;
                this.hasBusinessOwnerSurvey = data.hasBusinessOwnerSurvey;
                this.ssnRequired = !!data.ssnRequired;
                if (!data.allowSignups) this.searchPhysicians(this.companyId, this.token);
                else this.widgetReady = true;
            },
            signUp() {
                this.validateAccountSetUp();
            },
            addDependentAddresses(allDependents) {
                let self = this;
                allDependents.forEach(function (dependent, index) {
                    dependent.address = {...self.vm.mainModel.address};
                });
            },
            async validateAccountSetUp() {
                //Validate account info
                if (this.termsAccepted) {
                    this.acceptTermsError = false;
                } else {
                    this.acceptTermsError = true;
                    return;
                }

                if(this.hasReferralSurvey){
                    if (this.vm.mainModel.referralAnswer == null) {
                        notifications.showError({ message: "Please provide an answer to 'How did you hear about us?' question.", title: "Error" });
                        return;
                    }
                }

                if (this.ssnRequired) {
                    const digits = this.vm.mainModel.ssn ? String(this.vm.mainModel.ssn).replace(/\D/g, '') : '';
                    if (digits.length !== 9) {
                        notifications.showError({ message: "Social Security Number is required", title: "Error" });
                        return;
                    }
                }
                
                let result = await api.post('ProviderSearch', 'ValidateAccountCreation',
                    { ConsumerWizardVm: this.vm, spouse: this.spouse, children: this.children }, true);
                    
                if (result?.data?.success === true) {
                    this.completeSetUp();
                }
                else if (result?.data?.redirectToLogin === true) {
                    notifications.showError({ message: result.msg, title: "Error" });
                    setTimeout(function(){
                        common.goToLoginPage();
                    },2000)
                }
                else if(result != null){
                    notifications.showError({ message: result.msg, title: "Error" });
                }

            },
            async completeSetUp() {
                /*            let captchaCheck = await this.$refs.captcha.checkCaptcha();
                            if (captchaCheck == true) {*/
                if (this.allowDependents) {
                    if (!this.$refs.addDependents.validateDependents()) return;
                    let allDependents = this.$refs.addDependents.getAllDependents();
                    this.addDependentAddresses(allDependents);
                    let spouse = allDependents.filter(dependent => dependent.isSpouse == true);
                    let children = allDependents.filter(dependent => dependent.isSpouse != true);
                    this.spouse = spouse[0];
                    this.children = children;
                } else {
                    this.spouse = null;
                    this.children = [];
                }
                let data = {
                    consumerWizardVm: this.vm, spouse: this.spouse ? this.spouse : null, 
                    children: this.children, companyId: this.companyId, token: this.token
                };
                let result = await api.postDataAsJson('ProviderSearch', 'CompleteSignUpProcess', data);
                if (result) {
                    this.consumerId = result.data.consumerId;
                    this.loginData = result.data;
                    let redirectUrl = `/member/#/${this.consumerId}/dashboard`;
                    common.logUserInWithToken(this.loginData.token, this.loginData.userId, true, redirectUrl);
                }
                /* }*/
            },
        },
        watch: {
            termsAccepted(newVal) {
                if (!!newVal) this.acceptTermsError = false;
            },
        },
        async mounted() {
            await this.setVm();
        }
    }
</script>

<style scoped>
.consumer-wizard-root {
    padding-top: 10px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 3.5rem;
}

.consumer-wizard-tile {
    display: block;
    margin-bottom: 0;
}

.consumer-wizard-bar-card {
    padding: 0 !important;
    overflow: hidden;
    border-radius: 10px;
    flex-shrink: 0;
}
.consumer-wizard-bar-card >>> .bar-card-header-wrap {
    margin: 0;
}
.consumer-wizard-bar-card >>> .bar-card-body {
    margin: 0;
    padding: 16px 20px 20px 20px;
    box-sizing: border-box;
    border-radius: 0 0 10px 10px;
}

.consumer-wizard-terms-centered {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 1.5rem;
}

.consumer-wizard-terms-main {
    width: 100%;
    text-align: center;
}

.consumer-wizard-pill-wrap {
    flex-shrink: 0;
    text-align: center;
}

.wizard-form {
    background-color: #ffffff;
}
.wizard-form:not(.consumer-wizard-terms-form) {
    padding: 0;
}

.consumer-wizard-pill-wrap ::v-deep .dashboard-pill-btn {
    box-shadow: none !important;
    font-size: clamp(1rem, 0.92rem + 0.35vw, 1.125rem);
    padding: clamp(0.5rem, 0.42rem + 0.3vw, 0.5625rem) clamp(1rem, 0.85rem + 0.65vw, 1.25rem);
    line-height: 1.25 !important;
    font-weight: bold !important;
    border-width: 0.25rem !important;
    border-style: solid !important;
    -webkit-font-smoothing: antialiased;
    white-space: nowrap;
}

.consumer-wizard-pill-wrap ::v-deep .dashboard-pill-btn:hover,
.consumer-wizard-pill-wrap ::v-deep .dashboard-pill-btn:focus {
    box-shadow: none !important;
}

.wizard-form >>> .enrollment-signup-dob-field .k-datepicker.k-widget {
    width: 100% !important;
    max-width: 100% !important;
    display: block !important;
    box-sizing: border-box;
}
.wizard-form >>> .enrollment-signup-dob-field .k-datepicker .k-picker-wrap {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
}

.wizard-form input.form-control {
    background-color: #fafafa !important;
}
.wizard-form >>> .enrollment-signup-dob-field .k-datepicker .k-picker-wrap,
.wizard-form >>> .enrollment-signup-dob-field .k-datepicker .k-input,
.wizard-form >>> .enrollment-signup-dob-field .k-datepicker input[type="text"] {
    background-color: #fafafa !important;
    background-image: none !important;
}

.enrollment-signup-heading-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
    width: 100%;
}

.enrollment-signup-heading-text {
    flex: 1 1 auto;
    min-width: 0;
}

.enrollment-signup-help {
    flex-shrink: 0;
    text-decoration: none;
    font-size: 1rem;
    font-weight: 600;
}

@media screen and (max-width: 767px) {
    .consumer-wizard-pill-wrap {
        margin-top: 1rem;
    }
}

@media screen and (max-width: 460px) {
    .consumer-wizard-terms-main {
        height: 55px;
    }
}

@media screen and (max-width: 375px) {
    .consumer-wizard-terms-main {
        height: 80px;
    }
}

@media screen and (max-width: 330px) {
    .consumer-wizard-terms-main {
        height: 100px;
    }
}
</style>
